/**
 * Sub-Inventory Resolver
 *
 * Resolves sub_inventory references on LCI inputs. When an input has a
 * `sub_inventory` field pointing to another template, this resolver loads
 * that template, aggregates its per-functional-unit cost and TRACI scores,
 * and returns them so the TEA/LCA calculators can add them on top of the
 * base input's values.
 *
 * Cache: Resolved templates are cached in memory so repeated calls for the
 * same template_id are instant.
 */

import ipc from './ipc';
import { getImpactScores, getActiveImpactSource, DEFAULT_IMPACT_SOURCE } from './impactSource';

const cache = {};

/**
 * Load and aggregate a sub-inventory template.
 * Returns { cost_per_fu, traci_per_fu } where FU = functional_unit_kg.
 */
export async function resolveSubInventory(subInventory, impactSource = DEFAULT_IMPACT_SOURCE) {
  if (!subInventory || !subInventory.template_file) return null;

  // The aggregate differs per impact dataset, so the dataset is part of the key.
  const cacheKey = `${impactSource}::${subInventory.template_id || subInventory.template_file}`;
  if (cache[cacheKey]) return cache[cacheKey];

  try {
    const template = await ipc.invoke('load-template', subInventory.template_file);
    if (!template || !template.processes) return null;

    const fuKg = subInventory.functional_unit_kg || template.functional_unit?.weight_kg || 1.0;

    // Aggregate total cost and TRACI scores across all processes in the sub-inventory template
    let totalCost = 0;
    const totalTraci = {};

    for (const proc of Object.values(template.processes)) {
      for (const input of (proc.lci_inputs || [])) {
        const qty = input.quantity || 0;
        totalCost += qty * (input.cost_per_unit || 0);

        const scores = getImpactScores(input, impactSource);
        for (const [cat, val] of Object.entries(scores)) {
          totalTraci[cat] = (totalTraci[cat] || 0) + qty * (val || 0);
        }
      }
    }

    // Normalize to per-kg basis
    const result = {
      cost_per_fu: totalCost / fuKg,
      traci_per_fu: {},
    };
    for (const [cat, val] of Object.entries(totalTraci)) {
      result.traci_per_fu[cat] = val / fuKg;
    }

    cache[cacheKey] = result;
    return result;
  } catch (err) {
    console.error('[SubInventory] Failed to resolve:', cacheKey, err.message);
    return null;
  }
}

/**
 * Pre-resolve all sub_inventory references in a project.
 * Returns a map: { template_id: { cost_per_fu, traci_per_fu } }
 */
export async function resolveAllSubInventories(project) {
  if (!project?.processes) return {};

  const impactSource = getActiveImpactSource(project);

  const refs = new Set();
  for (const proc of Object.values(project.processes)) {
    for (const input of (proc.lci_inputs || [])) {
      if (input.sub_inventory?.template_file) {
        refs.add(JSON.stringify(input.sub_inventory));
      }
    }
  }

  const resolved = {};
  for (const refStr of refs) {
    const subInv = JSON.parse(refStr);
    const key = subInv.template_id || subInv.template_file;
    resolved[key] = await resolveSubInventory(subInv, impactSource);
  }

  return resolved;
}

/**
 * Get the additional cost per unit from a sub-inventory for a given input.
 * Uses pre-resolved data if available, otherwise returns 0.
 */
export function getSubInventoryCost(input, resolvedMap) {
  if (!input.sub_inventory) return 0;
  const key = input.sub_inventory.template_id || input.sub_inventory.template_file;
  const resolved = resolvedMap?.[key];
  return resolved?.cost_per_fu || 0;
}

/**
 * Get the additional TRACI scores per unit from a sub-inventory for a given input.
 * Uses pre-resolved data if available, otherwise returns {}.
 */
export function getSubInventoryTraci(input, resolvedMap) {
  if (!input.sub_inventory) return {};
  const key = input.sub_inventory.template_id || input.sub_inventory.template_file;
  const resolved = resolvedMap?.[key];
  return resolved?.traci_per_fu || {};
}

export default { resolveSubInventory, resolveAllSubInventories, getSubInventoryCost, getSubInventoryTraci };
